import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useApi } from '@/api/useApi'

import API from '@/api/apiUrls'
import { GEF } from '@/constants/giDataUploadCollection'

import { useGiPointStore } from '@/stores/giPoints'

import { useBaseStore } from '@haskoning/vue-basestore'
import { useSelectedItemMixin } from '@/stores/mixins/useSelectedItemMixin'

import type {
	GiDataUploadCollectionType,
	GiPointType,
	UploadFileType
} from '@/stores/type/giPoint.type'

const api = useApi<GiDataUploadCollectionType>()

const baseUrl = API.GI_DATA_UPLOAD_COLLECTIONS.INDEX
export const useGiDataUploadCollectionStore = defineStore('giDataUploadCollection', () => {
	const baseStore = useBaseStore<GiDataUploadCollectionType>({ service: baseUrl, api })
	const selectedItemMixin = useSelectedItemMixin<GiDataUploadCollectionType>(baseStore)

	const uploadLoading = ref(false)
	const showUploadDialog = ref(false)
	const uploadFileType = ref<UploadFileType>(GEF)
	const filterFields = ref(['name'])
	const useEPSG = ref<boolean>(false)
	const zoneNumber = ref<number | null>(null)
	const hemisphere = ref<'N' | 'S'>('N')

	const resetAll = () => {
		baseStore.items.value = []
		baseStore.selectedItems.value = []
		baseStore.currentItem.value = null
		uploadLoading.value = false
		showUploadDialog.value = false
		uploadFileType.value = GEF
	}

	const toggleUploadLoading = (value: boolean | null = null) => {
		uploadLoading.value = value ?? !uploadLoading.value
	}

	const updateGiPoints = async (giDataUploadCollection: GiDataUploadCollectionType) => {
		const giPointStore = useGiPointStore()
		giPointStore.resetAll()
		await giPointStore.fetchItems({ search: giDataUploadCollection.project }) as unknown as GiPointType[]
	}

	const postOrUpdate = async (item: FormData | null = null) => {
		try {
			const current = baseStore.currentItem.value
			if (item) {
				let response: any
				if (current && current.id) {
					item.append('id', current.id.toString())
					response = await formDataUpdate(current.id, item)
				} else {
					response = await baseStore.create(item)
				}
				if (response && !Array.isArray(response)) {
					await updateGiPoints(response)
				}
			}
		} catch (e) {
			console.error(e)
		}
	}

	async function formDataUpdate(itemId: number, item: FormData) {
		try {
			baseStore.writeLoading.value = true
			const response = await api.service(baseUrl).patch(itemId, item)
			const index = baseStore.items.value.findIndex((item) => item.id === response.id)
			if (index >= 0) {
				baseStore.items.value[index] = response
			}
		} catch (e) {
			baseStore.setSnackbarMessage('Update item failed.', 'error')
			console.error(e)
		} finally {
			baseStore.writeLoading.value = false
		}
	}

	const createOpenGround = async (
		formData: FormData
	): Promise<GiDataUploadCollectionType | null> => {
		let responseData: GiDataUploadCollectionType | null = null
		try {
			const createOpenGroundUrl = API.GI_DATA_UPLOAD_COLLECTIONS.CREATE_OPEN_GROUND
			responseData = (await api
				.service(createOpenGroundUrl)
				.create(formData)) as GiDataUploadCollectionType
			baseStore.items.value.push(responseData)
			await updateGiPoints(responseData)
		} catch (e) {
			console.error(e)
		}
		return responseData
	}

	const updateOpenGround = async (
		id: number,
		formData: FormData
	): Promise<GiDataUploadCollectionType | null> => {
		if (id) {
			try {
				const updateOpenGroundUrl = API.GI_DATA_UPLOAD_COLLECTIONS.UPDATE_OPEN_GROUND(id)
				const responseData = await api.service(updateOpenGroundUrl).post(formData)
				if (responseData) {
					const index = baseStore.items.value.findIndex((item) => item.id === responseData.id)
					if (index !== -1) {
						baseStore.items.value[index] = responseData
					}
					await updateGiPoints(responseData)
				}
			} catch (e) {
				console.error(e)
			}
		}
		return null
	}

	const toggleUploadDialog = (item: GiDataUploadCollectionType | null = null) => {
		if (item) {
			baseStore.currentItem.value = item
		}
		showUploadDialog.value = !showUploadDialog.value
	}

	const toggleChildSelectedItems = (id: number) => {
		const giPointStore = useGiPointStore()
		const item = baseStore.items.value.find((item) => item.id === id)
		const index = baseStore.selectedItems.value.findIndex((selectedItem) => selectedItem.id === id)
		if (item) {
			if (index !== -1) {
				giPointStore.selectAllItemsByCollectionId(item.id)
			} else {
				giPointStore.clearSelectedItemsByCollectionId(item.id)
			}
		}
	}

	const selectAllItems = () => {
		baseStore.selectedItems.value = [...baseStore.items.value]
		const giPointStore = useGiPointStore()
		baseStore.selectedItems.value.forEach((item) => {
			giPointStore.selectAllItemsByCollectionId(item.id)
		})
	}

	const clearSelectedItems = () => {
		baseStore.selectedItems.value = []
		const giPointStore = useGiPointStore()
		giPointStore.clearSelectedItems()
	}

	const giDataUploadCollectionOnGiPoint = (id: number): GiDataUploadCollectionType | undefined => {
		return baseStore.items.value.find((item) => item.id === id)
	}

	const hasCalculationCPTRelatedToSelectedItems = (): boolean => {
		const giPointStore = useGiPointStore()
		const giPoints: GiPointType[] = []
		baseStore.selectedItems.value.forEach((item) => {
			giPoints.push(...item.gipoint_set.features)
		})
		return giPointStore.hasCalculationCPTRelatedToSelectedItems(giPoints)
	}

	return {
		...baseStore,
		...selectedItemMixin,
		uploadLoading,
		showUploadDialog,
		uploadFileType,
		filterFields,
		useEPSG,
		zoneNumber,
		hemisphere,
		resetAll,
		toggleUploadLoading,
		updateGiPoints,
		postOrUpdate,
		createOpenGround,
		updateOpenGround,
		toggleUploadDialog,
		toggleChildSelectedItems,
		selectAllItems,
		clearSelectedItems,
		giDataUploadCollectionOnGiPoint,
		hasCalculationCPTRelatedToSelectedItems
	}
})
